import { levelMeta } from "@/lib/levels";
import { GardenPlant } from "@/components/client/Garden";
import { localToday } from "@/lib/habitColors";
import { useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, CartesianGrid } from "recharts";
import { Sparkles, Activity, Trophy, Compass, Download, Tags, UserMinus, TrendingUp, Printer } from "@/lib/icons";
import { Sprout } from "lucide-react";
import { ReflectionInsights } from "@/components/ReflectionInsights";
import { WellbeingTrends } from "@/components/WellbeingTrends";
import { TrackerManager } from "@/components/TrackerManager";
import { FocusPicker } from "@/components/HabitFocus";
import { ClinicianRubricRow } from "@/components/clinician/RubricPropose";
import { ClinicianStageRow } from "@/components/clinician/StagePropose";

const REWARD_COLORS = { motivating: "#059669", neutral: "#94A3B8", deterring: "#2f7a46", no_feedback: "#D6D3D1" };

function WorthAskingAbout({ reflections = [] }) {
  const open = reflections.filter((r) => r.status === "pending").slice(0, 5);
  if (!open.length) return null;
  return (
    <div className="mb-4 rounded-2xl bg-stone-50 border border-stone-200 p-4 text-sm" data-testid="worth-asking-about">
      <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-1">Worth asking about in session</div>
      <p className="text-xs text-stone-600 mb-2">The app doesn't press the member on missed weeks — these are open for you to raise gently if it feels right.</p>
      <ul className="space-y-1">
        {open.map((r) => (
          <li key={r.id} className="text-xs text-stone-800" data-testid={`worth-asking-${r.id}`}>
            <span className="font-semibold">{r.habit_title}</span> · {r.week} · {r.count}/{r.goal} logged
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Card({ icon: Icon, iconCls, title, children, testid }) {
  return (
    <div className="warm-card p-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className={`w-4 h-4 ${iconCls}`} />
        <h3 className="font-display font-bold text-stone-900">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export function ClientWeekRecap({ recap }) {
  if (!recap) return null;
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/70 px-4 py-3" data-testid="clinician-week-recap">
      <Sprout className="w-4 h-4 text-emerald-700 shrink-0" />
      <p className="flex-1 text-sm text-stone-800" data-testid="clinician-week-recap-text">
        <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-800 mr-2">Open on a positive</span>
        {recap.line.replace("your garden", "their garden").replace("whenever you are", "whenever they are")}
      </p>
    </div>
  );
}

export function ClientHeader({ detail, onExport, onUnlink, onPdf, includeNotes, onIncludeNotes }) {
  const lvl = detail.gamification.level;
  const { badge } = levelMeta(lvl.name);
  const lastCheckin = detail.client.last_checkin || detail.symptoms.map((s) => s.date).sort().slice(-1)[0];
  const daysAgo = lastCheckin ? Math.round((new Date(localToday()) - new Date(lastCheckin)) / 864e5) : null;
  const weekLogged = detail.habits.filter((h) => h.week_count > 0).length;
  const doneToday = detail.habits.filter((h) => h.completed_today).length;
  const stats = [
    { label: "Last check-in", v: daysAgo == null ? "never" : daysAgo === 0 ? "today" : `${daysAgo}d ago`, tone: daysAgo != null && daysAgo > 3 ? "text-amber-700" : "text-stone-900", testid: "hdr-last-checkin" },
    { label: "Habits this week", v: `${weekLogged}/${detail.habits.length}`, testid: "hdr-week-habits" },
    { label: "Today", v: `${doneToday}/${detail.habits.length}`, testid: "hdr-today" },
    { label: "Points", v: detail.gamification.points_total, testid: "hdr-points" },
    { label: "Rewards", v: detail.rewards.length, testid: "hdr-rewards" },
    { label: "Community · 30d", v: `Gave ${detail.kindness?.gave_30 ?? 0} · Got ${detail.kindness?.received_30 ?? 0}`, testid: "hdr-kindness", title: "Garden waters given and received from other members (anonymous handles only)" },
    ...(detail.ai_prefs && (!detail.ai_prefs.clinician || !detail.ai_prefs.self) ? [{ label: "AI", v: !detail.ai_prefs.clinician ? "Off · their choice" : "Off for them", testid: "hdr-ai-off", tone: "text-rose-800", title: !detail.ai_prefs.clinician ? "This member has turned off AI for your tools about them. You'll see the non-AI versions." : "This member has turned off AI for their own experience; your tools still may use the HIPAA lane." }] : []),
  ];
  return (
    <div className="warm-card p-5 sm:p-6" data-testid="client-header">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className={`w-16 h-20 rounded-2xl ${badge} ring-4 ring-white shadow grid place-items-center shrink-0 overflow-hidden`} title={`Level ${lvl.index} · ${lvl.name}`} data-testid="hdr-level-icon"><GardenPlant stage={lvl.index - 1} progress={lvl.progress} className="w-14 h-[72px]" animate={false} /></div>
          <div className="min-w-0">
            <h2 className="font-display text-2xl font-bold text-stone-900 truncate">{detail.client.name}</h2>
            <div className="text-sm text-stone-500 truncate">{detail.client.email} · Level {lvl.index} {lvl.name}</div>
            {detail.client.last_nudge_reply && (
              <div className={`mt-1 inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full ${detail.client.last_nudge_reply.reply === "hard_week" ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"}`} data-testid="hdr-nudge-reply">
                ↩ Replied to your nudge: {detail.client.last_nudge_reply.label}{detail.client.last_nudge_reply.note && <span className="font-normal italic">— "{detail.client.last_nudge_reply.note}"</span>}
                <span className="font-normal opacity-70">· {new Date(detail.client.last_nudge_reply.at).toLocaleDateString()}</span>
              </div>
            )}
            <div className="flex flex-wrap gap-x-4 mt-1">
              <span className="inline-flex items-center gap-2">
                <button data-testid="print-summary-btn" onClick={onPdf} className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 hover:text-amber-900"><Printer className="w-3.5 h-3.5" /> Print summary (PDF)</button>
                <label className="inline-flex items-center gap-1 text-[11px] text-stone-500 cursor-pointer" title="Adds a second page with your last 5 dated session notes — for your records only">
                  <input type="checkbox" checked={includeNotes} onChange={(e) => onIncludeNotes(e.target.checked)} data-testid="pdf-include-notes" className="accent-amber-700" /> include my notes
                </label>
              </span>
              <button data-testid="export-csv-btn" onClick={onExport} className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 hover:text-amber-900"><Download className="w-3.5 h-3.5" /> Export CSV</button>
              <button data-testid="unlink-client-btn" onClick={onUnlink} className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-400 hover:text-red-700"><UserMinus className="w-3.5 h-3.5" /> Step back from supporting them</button>
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {stats.map((m) => (
            <div key={m.label} className="text-center px-3 py-2 rounded-2xl bg-stone-50 border border-stone-200 min-w-[88px]" data-testid={m.testid} title={m.title}>
              <div className={`font-mono font-bold ${m.tone || "text-stone-900"} ${String(m.v).length > 8 ? "text-sm leading-7" : "text-lg"}`}>{m.v}</div>
              <div className="text-[9px] uppercase tracking-widest text-stone-500 font-bold">{m.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function AchievementsAndReflections({ detail }) {
  const g = detail.gamification;
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card icon={Trophy} iconCls="text-amber-700" title={`Achievements · ${g.achievements.length}/${g.catalog.length}`} testid="clinician-achievements">
        {g.achievements.length === 0 ? (
          <div className="text-sm text-stone-500">No achievements unlocked yet.</div>
        ) : (
          <ul className="space-y-2 max-h-56 overflow-y-auto">
            {g.achievements.map((a) => (
              <li key={a.id} className="flex items-center justify-between text-sm border-b border-stone-100 pb-2 last:border-0">
                <div>
                  <div className="font-semibold text-stone-800">{a.title}</div>
                  <div className="text-xs text-stone-500">{a.description}{a.habit_title ? ` · ${a.habit_title}` : ""}</div>
                </div>
                <span className="font-mono text-xs text-amber-800">+{a.points}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card icon={Compass} iconCls="text-amber-700" title="What gets in the way" testid="clinician-reflections">
        <WorthAskingAbout reflections={detail.reflections} />
        <ReflectionInsights insights={detail.reflection_insights} history={detail.reflections.filter((r) => r.status === "answered")} forClinician />
      </Card>
    </div>
  );
}

export function ChartsRow({ detail }) {
  const rewardChart = Object.entries(detail.reward_stats).map(([k, v]) => ({ name: k.replace("_", " "), value: v, key: k }));
  const symptomChart = detail.symptoms.slice(-30).map((s) => ({
    date: new Date(s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    mood: s.mood, anxiety: s.anxiety, panic: s.panic_attack ? s.panic_severity : 0,
  }));
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card icon={Sparkles} iconCls="text-amber-700" title="Reward effectiveness">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={rewardChart}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E8E2D8" />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#78716C" }} />
            <YAxis tick={{ fontSize: 11, fill: "#78716C" }} allowDecimals={false} />
            <Tooltip cursor={{ fill: "#FEF3C7" }} />
            <Bar dataKey="value" radius={[8, 8, 0, 0]}>{rewardChart.map((e) => <Cell key={e.key} fill={REWARD_COLORS[e.key]} />)}</Bar>
          </BarChart>
        </ResponsiveContainer>
        <div className="text-xs text-stone-500 mt-2">See which rewards land, which don't move the needle, and which to skip next time.</div>
      </Card>
      <Card icon={Activity} iconCls="text-emerald-700" title="Mood, anxiety & panic trend">
        {symptomChart.length === 0 ? (
          <div className="text-sm text-stone-500 py-16 text-center">No symptom logs yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={symptomChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E2D8" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#78716C" }} />
              <YAxis domain={[0, 10]} tick={{ fontSize: 11, fill: "#78716C" }} />
              <Tooltip />
              <Line type="monotone" dataKey="mood" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="anxiety" stroke="#d9467a" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="panic" stroke="#2f7a46" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
        {detail.symptom_tag_counts?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-stone-100" data-testid="symptom-tag-counts">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-2"><Tags className="w-3 h-3" /> Most reported symptoms</div>
            <div className="flex flex-wrap gap-1.5">
              {detail.symptom_tag_counts.slice(0, 8).map((t) => (
                <span key={t.tag} className="text-xs px-2.5 py-1 rounded-full bg-stone-100 text-stone-700">{t.tag} <span className="font-mono text-amber-800">×{t.count}</span></span>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

export function WellbeingRow({ detail, onAssign, onArchive }) {
  const defs = detail.scale_defs || [];
  const avgs = detail.scale_avgs || {};
  const avgDefs = useMemo(() => (detail.scale_defs || []).filter((d) => (detail.scale_avgs || {})[d.key] != null), [detail.scale_defs, detail.scale_avgs]);
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <WellbeingTrends symptoms={detail.symptoms} defs={defs} trackers={detail.trackers || []} title="Wellbeing scales & custom trackers" height={220} />
        {Object.keys(avgs).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="scale-avgs">
            <span className="text-[10px] font-bold uppercase tracking-widest text-stone-500 self-center mr-1">30-log averages</span>
            {avgDefs.map((d) => (
              <span key={d.key} className="text-xs px-2.5 py-1 rounded-full bg-stone-100 text-stone-700">{d.label} <span className="font-mono text-amber-800">{avgs[d.key]}</span></span>
            ))}
          </div>
        )}
      </div>
      <div className="space-y-3">
        <TrackerManager trackers={detail.trackers || []} onCreate={onAssign} onArchive={onArchive} addLabel="Assign"
          subtitle="Assign something specific for this member to log daily — medication taken, hours of sleep, urges." />
        {detail.tracker_summary?.length > 0 && (
          <div className="warm-card p-4" data-testid="tracker-summary">
            <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-2">All-time tracker summary</div>
            <ul className="space-y-1">
              {detail.tracker_summary.map((t) => (
                <li key={t.tracker_id} className="text-xs text-stone-700"><span className="font-semibold">{t.name}</span> — {t.summary} <span className="text-stone-400">({t.count} logs)</span></li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export function HabitsCard({ habits, stageRequests = [], stageHeadsup = [], onFocus, clientId, onChanged }) {
  return (
    <Card icon={TrendingUp} iconCls="text-amber-700" title="Habits, focus & measurement" testid="clinician-habits-card">
      <p className="text-xs text-stone-500 -mt-2 mb-3">Set one Keystone habit to concentrate rewards and reminders. Propose how a habit is measured — the member chooses whether to adopt it.</p>
      <ul className="space-y-2">
        {habits.map((h) => (
          <li key={h.id} className="flex items-start justify-between gap-3 text-sm border-b border-stone-100 pb-3 last:border-0" data-testid={`clinician-habit-${h.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-stone-800">{h.title}</div>
              {onFocus && <div className="mt-1"><FocusPicker value={h.focus} habitId={`c-${h.id}`} compact onChange={(f) => onFocus(h, f)} /></div>}
              <div className="text-xs text-stone-500 capitalize">
                {h.kind === "start" ? "Starting" : "Easing off"} • goal {h.goal_label?.toLowerCase()} (step {h.phase_index + 1}/{h.ladder?.length}) • this week {h.week_count}/{h.goal}{h.streak > 0 ? ` • ${h.streak}-day streak` : ""}
              </div>
              {clientId && <ClinicianRubricRow clientId={clientId} habit={h} onSent={onChanged} />}
              {clientId && <ClinicianStageRow clientId={clientId} habit={h} requested={(stageRequests || []).some((r) => r.habit_id === h.id)} headsup={(stageHeadsup || []).find((s) => s.habit_id === h.id)} onSent={onChanged} />}
            </div>
            <span className="font-mono text-amber-800 font-bold">{h.total_completions}</span>
          </li>
        ))}
        {habits.length === 0 && <div className="text-sm text-stone-500">No active habits.</div>}
      </ul>
    </Card>
  );
}
